import React, { useState, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/navigation/Header';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import Icon from '../../components/AppIcon';

import { AuthContext } from '../../contexts/AuthContext';
import ViolationStatusOverview from './components/ViolationStatusOverview';
import ViolationManagementInterface from './components/ViolationManagementInterface';
import InteractiveViolationTimeline from './components/InteractiveViolationTimeline';
import EnhancedRemediationWizard from './components/EnhancedRemediationWizard';
import violationDetectionService from '../../services/violationDetectionService';
import { supabase } from '../../lib/supabase';

const ClientViolationAlertResponsePortal = () => {
  const { user } = useContext(AuthContext);
  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState(null);
  const [selectedViolation, setSelectedViolation] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [realtimeChannel, setRealtimeChannel] = useState(null);

  useEffect(() => {
    loadUserCompany();

    return () => {
      if (realtimeChannel) {
        violationDetectionService?.unsubscribe(realtimeChannel);
      }
    };
  }, []);

  useEffect(() => {
    if (companyId) {
      loadClientViolations();
      setupRealtimeSubscription();
    }
  }, [companyId]);

  const loadUserCompany = async () => {
    try {
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('company_id')
        ?.eq('email', user?.email)
        ?.single();

      setCompanyId(profile?.company_id);
    } catch (error) {
      console.error('Error loading user company:', error);
    }
  };

  const loadClientViolations = async () => {
    try {
      setLoading(true);
      const data = await violationDetectionService?.getViolationsByClient(companyId);
      setViolations(data);
    } catch (error) {
      console.error('Failed to load violations:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = violationDetectionService?.subscribeToViolations(
      async (violation, eventType) => {
        // Check if violation affects this company
        const { data: employees } = await supabase
          ?.from('employees')
          ?.select('id')
          ?.eq('company_id', companyId);

        const employeeIds = employees?.map(e => e?.id) || [];
        const isRelevant = violation?.affectedEmployeeIds?.some(id => employeeIds?.includes(id));

        if (isRelevant) {
          if (eventType === 'INSERT') {
            setViolations(prev => [violation, ...prev]);
          } else if (eventType === 'UPDATE') {
            setViolations(prev => 
              prev?.map(v => v?.id === violation?.id ? violation : v)
            );
          }
        }
      }
    );
    setRealtimeChannel(channel);
  };

  const handleStartRemediation = (violation) => {
    setSelectedViolation(violation);
    setShowWizard(true);
    setShowTimeline(false);
  };

  const handleViewTimeline = (violation) => {
    setSelectedViolation(violation);
    setShowTimeline(true);
    setShowWizard(false);
  };

  const handleCloseWizard = () => {
    setShowWizard(false);
    setSelectedViolation(null);
    loadClientViolations();
  };

  const handleCloseTimeline = () => {
    setShowTimeline(false);
    setSelectedViolation(null);
  };

  const activeViolations = violations?.filter(v => v?.violationStatus === 'identified' || v?.violationStatus === 'in_progress') || [];
  const resolvedViolations = violations?.filter(v => v?.violationStatus === 'completed') || [];
  const complianceScore = violations?.length > 0 
    ? Math.max(0, 100 - (violations?.filter(v => v?.severity === 'critical')?.length * 10) - (violations?.filter(v => v?.severity === 'high')?.length * 5))
    : 100;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ComplianceAlertBanner />
      <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Page Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="Bell" size={32} color="var(--color-primary)" />
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground">
                Client Violation Alert & Response Portal
              </h1>
            </div>
            <p className="caption text-sm md:text-base text-muted-foreground">
              AI-powered violation detection with interactive timeline and step-by-step remediation wizard - reducing response time from hours to minutes
            </p>
          </div>

          {/* Main Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Violation Status Overview - 4 cols */}
            <div className="lg:col-span-4">
              <ViolationStatusOverview 
                activeViolations={activeViolations?.length}
                resolvedViolations={resolvedViolations?.length}
                complianceScore={complianceScore}
                loading={loading}
              />
            </div>

            {/* Main Response Panel - 8 cols */}
            <div className="lg:col-span-8">
              <ViolationManagementInterface 
                violations={violations}
                loading={loading}
                onStartRemediation={handleStartRemediation}
                onViewTimeline={handleViewTimeline}
                onRefresh={loadClientViolations}
              />
            </div>
          </div>
        </motion.div>
      </main>
      {/* Enhanced AI-Powered Remediation Wizard Modal */}
      {showWizard && selectedViolation && (
        <EnhancedRemediationWizard 
          violation={selectedViolation}
          onClose={handleCloseWizard}
        />
      )}
      {/* Interactive Timeline Modal */}
      {showTimeline && selectedViolation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
              <h2 className="font-heading font-bold text-lg text-foreground">Violation Timeline</h2>
              <button onClick={handleCloseTimeline} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={24} />
              </button>
            </div>
            <div className="p-4">
              <InteractiveViolationTimeline violation={selectedViolation} />
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ClientViolationAlertResponsePortal;